import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Modal, message } from "antd";
import ReactPlayer from "react-player";
import styles from "./CourseLanding.module.scss";

// Лендинг курса (вариант E): тёмная витрина + навык и рынок + путь по модулям +
// поля, которые заполняет учитель. Используется и у ученика (front_web), и в
// предпросмотре учителя (front_edu) - файл одинаковый в обоих репозиториях,
// отличаются только обёртки, которые передают api и действия.

const pad = (n) => String(n).padStart(2, "0");
const formatDate = (value) => {
  if (!value) return null;
  // черновик курса у учителя хранит даты как «дд.мм.гггг»
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(String(value))) return String(value);
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
};
const formatRub = (n) => `${Number(n).toLocaleString("ru-RU")} ₽`;
const formatMonth = (value) => {
  const d = value ? new Date(value) : null;
  return d && !Number.isNaN(d.getTime())
    ? d.toLocaleDateString("ru-RU", { month: "long", year: "numeric" })
    : "";
};
const plural = (n, one, few, many) => {
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (a > 10 && a < 20) return many;
  if (b > 1 && b < 5) return few;
  if (b === 1) return one;
  return many;
};
// Тексты курсов часто сгенерированы ИИ и содержат markdown-разметку **жирный**
const stripMd = (text) => String(text || "").replace(/\*\*/g, "").trim();
const stepTitle = (text) => stripMd(text).replace(/^\d+\s*[.)]\s*/, "").replace(/:\s*$/, "");
const renderMd = (text) =>
  String(text || "")
    .split(/\*\*([^*]+)\*\*/g)
    .map((part, i) => (i % 2 ? <strong key={i}>{part}</strong> : part.replace(/\*\*/g, "")));
const whatsappLink = (phone) => {
  const digits = String(phone || "").replace(/\D/g, "");
  return digits ? `https://wa.me/${digits}` : null;
};

const IconPlay = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="#1667FF" aria-hidden="true">
    <path d="M8 5v14l11-7z" />
  </svg>
);
const IconSpark = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" />
    <path d="M19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8z" />
  </svg>
);

export const CourseLanding = ({
  data,
  api = {},
  onBuy,
  onShare,
  onBack,
  buyLabel,
  isPreview = false,
  viewerName,
}) => {
  const [market, setMarket] = useState([]);
  const [reviews, setReviews] = useState({ rating: null, count: 0, items: [] });
  const [videoOpen, setVideoOpen] = useState(false);
  const heroRef = useRef(null);
  const [heroMinHeight, setHeroMinHeight] = useState(undefined);

  // Витрина заполняет первый экран целиком: высота окна минус всё, что над ней
  // (шапка сайта, липкая панель курса). Шапки в web и edu разные - поэтому меряем.
  useLayoutEffect(() => {
    const measure = () => {
      const el = heroRef.current;
      if (!el) return;
      let scrolled = window.scrollY;
      for (let p = el.parentElement; p; p = p.parentElement) scrolled += p.scrollTop;
      const top = el.getBoundingClientRect().top + scrolled;
      setHeroMinHeight(Math.max(0, window.innerHeight - top));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);
  const [ask, setAsk] = useState({ name: "", phone: "", text: "" });
  const [askSent, setAskSent] = useState(false);

  const skills = useMemo(() => {
    const fromLearn = (data?.quest_skills || [])
      .filter((s) => s?.speciality_tag?.name)
      .map((s) => ({ id: s.speciality_tag.id, name: s.speciality_tag.name, description: s.description }));
    const known = new Set(fromLearn.map((s) => s.id));
    const fromTags = (data?.speciality_tag || [])
      .filter((t) => t?.name && !known.has(t.id))
      .map((t) => ({ id: t.id, name: t.name, description: "" }));
    return [...fromLearn, ...fromTags];
  }, [data]);
  const mainSkill = skills[0];
  const skillIds = skills.map((s) => s.id).filter(Boolean).join(",");

  useEffect(() => {
    if (!skillIds || !api.getSkillsMarket) return;
    api
      .getSkillsMarket(skillIds)
      .then((res) => setMarket(Array.isArray(res?.data) ? res.data : []))
      .catch(() => setMarket([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skillIds]);

  useEffect(() => {
    if (!data?.ses || !api.getReviews) return;
    api
      .getReviews(data.ses)
      .then((res) => res?.data && setReviews(res.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.ses]);

  const stages = data?.stages || [];
  const speakers = data?.quest_speakers || [];
  const audience = data?.quest_recommendations || [];
  const price = Number(data?.participateprice) || 0;
  const hours = data?.academicHours;
  const dateBegin = formatDate(data?.dateBegin);
  const dateEnd = formatDate(data?.dateEnd);
  const banner = data?.banner || data?.y_image;
  const video = data?.demo_youtube || data?.demo_video;
  const counts = data?.progress || data || {};
  const tasks = (counts.count_task || 0) + (counts.count_text_task || 0) + (counts.count_code_task || 0);
  const formats = [
    { n: counts.count_video, label: plural(counts.count_video, "видео", "видео", "видео"), text: "Теория и практика на задачах, приближенных к реальным." },
    { n: counts.count_article, label: plural(counts.count_article, "статья", "статьи", "статей"), text: "Текстовые материалы с любого устройства." },
    { n: counts.count_quiz, label: plural(counts.count_quiz, "тест", "теста", "тестов"), text: "Проверка знаний после модулей." },
    { n: tasks, label: plural(tasks, "задание", "задания", "заданий"), text: "Практика с обратной связью." },
    { n: counts.count_scorm_task, label: plural(counts.count_scorm_task, "интерактив", "интерактива", "интерактивов"), text: "Тренажёры внутри уроков." },
    { n: speakers.length, label: plural(speakers.length, "спикер", "спикера", "спикеров"), text: "Обучение с участием экспертов." },
  ].filter((f) => f.n > 0);

  const marketWithData = market.filter((m) => m.vacancies_count != null);
  const mainMarket = marketWithData.find((m) => m.id === mainSkill?.id);
  const vacancies = mainMarket
    ? mainMarket.vacancies_count
    : marketWithData.reduce((max, m) => Math.max(max, m.vacancies_count || 0), 0);
  const salarySource = market.find((m) => m.id === mainSkill?.id && m.salary_median) || market.find((m) => m.salary_median);
  const professions = [...new Set(market.flatMap((m) => m.professions || []))].slice(0, 5);
  const marketDate = formatMonth(marketWithData.map((m) => m.updated_at).filter(Boolean).sort().pop());
  const hasMarket = marketWithData.length > 0;
  const withCertificate = !!data?.is_generate_certificate;

  const facts = [
    hours ? { v: `${hours} ак. ч`, l: "длительность" } : null,
    stages.length ? { v: String(stages.length), l: plural(stages.length, "модуль", "модуля", "модулей") } : null,
    dateBegin ? { v: dateEnd ? `${dateBegin} — ${dateEnd}` : `с ${dateBegin}`, l: "даты обучения" } : null,
    { v: "Онлайн", l: "формат" },
    withCertificate ? { v: "Именной", l: "сертификат" } : null,
  ].filter(Boolean);

  const buyText = buyLabel || (price > 0 ? `Купить за ${formatRub(price)}` : "Записаться бесплатно");
  const handleBuy = () => {
    if (isPreview) return message.info("Это предпросмотр лендинга");
    onBuy && onBuy();
  };
  const handleShare = () => {
    if (isPreview) return message.info("Это предпросмотр лендинга");
    onShare && onShare();
  };
  const sendQuestion = (e) => {
    e.preventDefault();
    if (isPreview) return message.info("Это предпросмотр лендинга");
    if (!ask.name.trim()) return message.error("Укажите имя");
    if (!ask.phone.trim()) return message.error("Укажите телефон");
    if (!ask.text.trim()) return message.error("Напишите вопрос");
    if (!api.postQuestion) return;
    api
      .postQuestion({ ses: data.ses, name: ask.name.trim(), phone: ask.phone.trim(), text: ask.text.trim() })
      .then(() => {
        message.success("Вопрос отправлен");
        setAskSent(true);
        setAsk({ name: "", phone: "", text: "" });
      })
      .catch(() => message.error("Не удалось отправить вопрос"));
  };
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (!data) return null;
  const wa = whatsappLink(data.phone);

  return (
    <div className={styles.root}>
      <div className={styles.topbar}>
        <div className={styles.topbarInner}>
          <div className={styles.topbarTitle}>{stripMd(data.name)}</div>
          {hours ? <div className={styles.topbarMeta}>{hours} академ. часов</div> : null}
          <div className={styles.topbarPrice}>{price > 0 ? formatRub(price) : "Бесплатно"}</div>
          <button type="button" className={styles.btnPrimarySm} onClick={handleBuy}>
            {price > 0 ? "Купить курс" : "Записаться"}
          </button>
        </div>
      </div>

      <section ref={heroRef} className={styles.hero} style={{ minHeight: heroMinHeight }}>
        <div className={styles.heroInner}>
          <div className={styles.heroText}>
            {onBack && (
              <button type="button" className={styles.backLink} onClick={onBack}>
                ← Каталог курсов
              </button>
            )}
            <div className={styles.chips}>
              {mainSkill && (
                <span className={styles.heroChip}>
                  <span className={styles.dot} />
                  Навык: {mainSkill.name}
                </span>
              )}
              {reviews.count > 0 && (
                <span className={styles.heroChip}>
                  <span className={styles.star}>★</span> {reviews.rating} · {reviews.count}{" "}
                  {plural(reviews.count, "отзыв", "отзыва", "отзывов")}
                </span>
              )}
            </div>
            <h1 className={styles.heroTitle}>{stripMd(data.name)}</h1>
            {data.description && <p className={styles.heroLead}>{stripMd(data.description)}</p>}
            <div className={styles.heroActions}>
              <button type="button" className={styles.btnPrimary} onClick={handleBuy}>
                {buyText}
              </button>
              {stages.length > 0 && (
                <button type="button" className={styles.btnGhost} onClick={() => scrollTo("section3")}>
                  Путь по навыку
                </button>
              )}
              <button type="button" className={styles.btnGhostQuiet} onClick={handleShare}>
                Поделиться
              </button>
            </div>
          </div>
          <div className={styles.heroMedia} style={banner ? { backgroundImage: `url(${banner})` } : undefined}>
            {video && (
              <button type="button" className={styles.playBtn} aria-label="Смотреть демо-видео" onClick={() => setVideoOpen(true)}>
                <IconPlay />
              </button>
            )}
            {video && <span className={styles.mediaLabel}>Демо-видео</span>}
          </div>
        </div>
        <div className={styles.factsWrap}>
          <div className={styles.facts}>
            {facts.map((f) => (
              <div key={f.l} className={styles.fact}>
                <div className={styles.factValue}>{f.v}</div>
                <div className={styles.factLabel}>{f.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <main className={styles.main}>
        {mainSkill && (
          <section className={hasMarket ? styles.grid2 : undefined}>
            <div className={styles.card}>
              <div className={styles.overline}>Навык</div>
              <div className={styles.skillHead}>
                <div className={styles.skillIcon}>
                  <IconSpark />
                </div>
                <div className={styles.skillName}>{mainSkill.name}</div>
              </div>
              <div className={styles.levels}>
                <div className={styles.levelRow}>
                  <span className={styles.muted}>Сейчас</span>
                  <span>Не подтверждён</span>
                </div>
                <div className={styles.bar}>
                  <div className={styles.barNow} />
                </div>
                <div className={styles.levelRow}>
                  <span className={styles.muted}>После курса</span>
                  <span className={styles.accentText}>{withCertificate ? "Подтверждён сертификатом" : "Изучен на курсе"}</span>
                </div>
                <div className={styles.bar}>
                  <div className={styles.barAfter} />
                </div>
              </div>
              {skills.length > 1 && (
                <div className={styles.chipsRow}>
                  {skills.slice(1, 7).map((s) => (
                    <span key={s.id} className={styles.softChip}>
                      {s.name}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {hasMarket && (
              <div className={styles.darkCard}>
                <div className={styles.darkCardTitle}>Где нужен навык</div>
                <div className={styles.statGrid}>
                  <div className={styles.stat}>
                    <div className={styles.statValue}>{vacancies.toLocaleString("ru-RU")}</div>
                    <div className={styles.statLabel}>{plural(vacancies, "вакансия", "вакансии", "вакансий")} с навыком</div>
                  </div>
                  {salarySource && (
                    <div className={styles.stat}>
                      <div className={styles.statValue}>{formatRub(salarySource.salary_median)}</div>
                      <div className={styles.statLabel}>медианная зарплата</div>
                    </div>
                  )}
                </div>
                {professions.length > 0 && (
                  <div className={styles.chipsRow}>
                    {professions.map((p) => (
                      <span key={p} className={styles.darkChip}>
                        {p}
                      </span>
                    ))}
                  </div>
                )}
                <div className={styles.source}>По данным hh.ru{marketDate ? `, ${marketDate}` : ""}</div>
              </div>
            )}
          </section>
        )}

        {stages.length > 0 && (
          <section id="section3" className={styles.card}>
            <h2 className={styles.h2}>Путь по навыку</h2>
            <p className={styles.sub}>
              {stages.length} {plural(stages.length, "модуль", "модуля", "модулей")}
              {hours ? ` · ${hours} академ. часов` : ""}
            </p>
            <div className={styles.steps}>
              {stages.map((s, i) => (
                <div key={s.id || i} className={styles.step}>
                  <div className={styles.stepHead}>Шаг {i + 1}</div>
                  <div className={styles.stepTitle}>{stepTitle(s.name)}</div>
                  <div className={styles.stepBar}>
                    <div style={{ width: `${Math.round(((i + 1) / stages.length) * 100)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {(data.content_description || skills.some((s) => s.description)) && (
          <section id="section6" className={styles.card}>
            <h2 className={styles.h2}>О курсе</h2>
            {data.content_description && <p className={styles.prose}>{renderMd(data.content_description)}</p>}
            {skills.some((s) => s.description) && (
              <>
                <h3 className={styles.h3}>Чему вы научитесь</h3>
                <div className={styles.tiles}>
                  {skills
                    .filter((s) => s.description)
                    .map((s) => (
                      <div key={s.id} className={styles.tile}>
                        <span className={styles.accentChip}>{s.name}</span>
                        <div className={styles.muted}>{stripMd(s.description)}</div>
                      </div>
                    ))}
                </div>
              </>
            )}
          </section>
        )}

        {(audience.length > 0 || formats.length > 0) && (
          <section className={styles.grid2}>
            {audience.length > 0 && (
              <div id="section4" className={styles.card}>
                <h2 className={styles.h2sm}>Кому подойдёт</h2>
                {audience.map((a, i) => (
                  <div key={a.id || i} className={styles.listItem}>
                    <div className={styles.strong}>{stripMd(a.title)}</div>
                    {a.description && <div className={styles.muted}>{stripMd(a.description)}</div>}
                  </div>
                ))}
              </div>
            )}
            {formats.length > 0 && (
              <div id="section2" className={styles.card}>
                <h2 className={styles.h2sm}>Формат обучения</h2>
                <div className={styles.formatGrid}>
                  {formats.map((f) => (
                    <div key={f.label} className={styles.formatItem} title={f.text}>
                      <div className={styles.formatValue}>{f.n}</div>
                      <div>{f.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {speakers.length > 0 && (
          <section id="section1" className={styles.card}>
            <h2 className={styles.h2}>Спикеры</h2>
            <div className={styles.speakers}>
              {speakers.map((s, i) => (
                <div key={s.id || i} className={styles.speaker}>
                  <div className={styles.avatar} style={s.avatar ? { backgroundImage: `url(${s.avatar})` } : undefined}>
                    {!s.avatar && `${(s.first_name || " ")[0]}${(s.last_name || " ")[0]}`.trim()}
                  </div>
                  <div className={styles.speakerText}>
                    <div className={styles.strong}>
                      {s.first_name} {s.last_name}
                    </div>
                    {s.position && <div className={styles.accentText}>{s.position}</div>}
                    {s.description && <div className={styles.muted}>{stripMd(s.description)}</div>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {withCertificate && (
        <section className={styles.certBlock}>
          <div className={styles.certText}>
            <h2 className={styles.certTitle}>Сертификат, который не стыдно показать</h2>
            <p className={styles.certLead}>
              Именной, с номером для проверки. Выдаётся после прохождения курса и подтверждает навык в резюме Skillometer.
            </p>
            <button type="button" className={styles.btnPrimary} onClick={handleBuy}>
              Начать обучение
            </button>
          </div>
          <div className={styles.certSample} aria-hidden="true">
            <div className={styles.certNo}>СЕРТИФИКАТ № 000123</div>
            <div className={styles.certName}>{viewerName || "Ваше имя и фамилия"}</div>
            <div className={styles.certNote}>
              прошёл(а) курс{hours ? ` · ${hours} академических часов` : ""}
            </div>
            <div className={styles.certCourse}>«{stripMd(data.name)}»</div>
          </div>
        </section>
        )}

        {reviews.items?.length > 0 && (
          <section className={styles.card}>
            <div className={styles.rowBetween}>
              <h2 className={styles.h2}>Отзывы участников</h2>
              <div className={styles.muted}>
                <span className={styles.ratingBig}>{reviews.rating}</span> из 5 · {reviews.count}{" "}
                {plural(reviews.count, "отзыв", "отзыва", "отзывов")}
              </div>
            </div>
            <div className={styles.reviews}>
              {reviews.items.map((r, i) => (
                <div key={i} className={styles.review}>
                  <div className={styles.stars}>{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</div>
                  <div>{r.text}</div>
                  <div className={styles.mutedSm}>
                    {r.author}
                    {formatDate(r.created_at) ? `, ${formatDate(r.created_at)}` : ""}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <section id="course-ask" className={`${styles.card} ${styles.askBlock}`}>
          <div className={styles.askText}>
            <h2 className={styles.h2}>Остались вопросы?</h2>
            <p className={styles.muted}>Спикеры курса ответят на ваш вопрос.</p>
            {wa && (
              <a className={styles.waLink} href={wa} target="_blank" rel="noreferrer">
                Написать в WhatsApp
              </a>
            )}
            {data.email && (
              <a className={styles.mailLink} href={`mailto:${data.email}`}>
                {data.email}
              </a>
            )}
          </div>
          <form className={styles.askForm} onSubmit={sendQuestion}>
            <label className={styles.field}>
              Имя
              <input value={ask.name} onChange={(e) => setAsk({ ...ask, name: e.target.value })} />
            </label>
            <label className={styles.field}>
              Телефон
              <input type="tel" value={ask.phone} onChange={(e) => setAsk({ ...ask, phone: e.target.value })} />
            </label>
            <label className={`${styles.field} ${styles.fieldWide}`}>
              Ваш вопрос
              <textarea rows={3} value={ask.text} onChange={(e) => setAsk({ ...ask, text: e.target.value })} />
            </label>
            <button type="submit" className={`${styles.btnPrimary} ${styles.fieldWide}`} disabled={askSent}>
              {askSent ? "Вопрос отправлен" : "Отправить вопрос"}
            </button>
          </form>
        </section>
      </main>

      <Modal visible={videoOpen} open={videoOpen} onCancel={() => setVideoOpen(false)} footer={null} width={960} destroyOnClose centered>
        {video && <ReactPlayer url={video} controls playing width="100%" height="520px" />}
      </Modal>
    </div>
  );
};
